/**
 * #44 Yazışma Denetimi — ekip mesajlaşmasının back office arşivi.
 *
 * İKİ GÖRÜNÜM tek ekranda, sekme ile: "Sohbetler" (yazışma arşivi) ve "İhlaller" (yasaklı
 * kelime yakalanan mesajlar). Her görünümün kendi OData ucu ve kendi c360 hook'u var —
 * useC360List tek apiBase alır, mod değişince bileşen değişir (hook remount olur).
 *
 * HAM METİN: sohbet akışı mesaj tablosundan okunur ve o tablo yalnız MASKELENMİŞ metni taşır.
 * Kullanıcının yazdığı ham metin sadece ihlal kaydında durur; bu ekranda ihlal satırının
 * detayında görünür.
 *
 * c360 liste altyapısı üzerine kuruludur (repo standardı).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, Toast, ExcelIcon, exportToExcel, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import { useC360List, C360Table, ColumnChooser, ViewIcon } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/teamchatarchive';
const API_VIOLATIONS = '/api/teamchatarchive/violations';
const LIST_ROUTE = '/team-chat-archive';

const KIND_LABELS = { DIRECT: 'Birebir', TEAM_GROUP: 'Ekip grubu' };

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR');
}

export function TeamChatArchivePage() {
  const [mode, setMode] = useState('conversations');
  const [toast, setToast] = useState(null);
  usePageMeta('Yazışma Denetimi', 'Ekip içi mesajlaşma arşivi ve yasaklı kelime ihlalleri.');

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  return (
    <>
      {mode === 'conversations'
        ? <ConversationsView mode={mode} setMode={setMode} showToast={showToast} />
        : <ViolationsView mode={mode} setMode={setMode} showToast={showToast} />}
      <Toast toast={toast} />
    </>
  );
}

/** Sekme şeridi — iki görünüm arasında geçiş. */
function ModeTabs({ mode, setMode }) {
  const tab = (value, label) => (
    <button
      key={value}
      type="button"
      style={mode === value ? tabActiveStyle : tabStyle}
      onClick={() => setMode(value)}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'inline-flex', gap: 4 }}>
      {tab('conversations', 'Sohbetler')}
      {tab('violations', 'İhlaller')}
    </div>
  );
}

// ───────────────────────── Sohbetler ─────────────────────────

const CONV_FILTER_META = {
  title: { type: 'text', odataField: 'Title' },
  lastMessagePreview: { type: 'text', odataField: 'LastMessagePreview' },
};

function ConversationsView({ mode, setMode, showToast }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const searchFields = useMemo(() => ['Title', 'LastMessagePreview'], []);

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    exportRows,
  } = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: CONV_FILTER_META,
    searchFields,
    defaultSortField: 'LastMessageDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const openDetail = (row) => {
    if (!row) return;
    navigate(`${LIST_ROUTE}/${row.id}`, { state: { row } });
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Sohbet', meta: { filterType: 'text' },
      cell: (i) => i.getValue() || '(birebir)',
    }),
    columnHelper.accessor('kind', {
      header: 'Tip', enableColumnFilter: false,
      // Sunucu Kind'ı int tutar (1 = birebir, 2 = ekip grubu); DTO'suz OData ham değeri döner.
      cell: (i) => (i.getValue() === 2 ? KIND_LABELS.TEAM_GROUP : KIND_LABELS.DIRECT),
    }),
    columnHelper.accessor('lastMessagePreview', {
      header: 'Son Mesaj', meta: { filterType: 'text' },
      cell: (i) => i.getValue() || '—',
    }),
    columnHelper.accessor('lastMessageDate', {
      header: 'Son Mesaj Zamanı', enableColumnFilter: false,
      cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.accessor('createdDate', {
      header: 'Açılış', enableColumnFilter: false,
      cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <button type="button" style={s.iconBtn} title="Yazışmayı aç"
          onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}>
          <ViewIcon />
        </button>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <ListPageShell
      title="Yazışma Denetimi"
      subtitle="Ekip içi mesajlaşma arşivi. Mesajlar maskelenmiş hâliyle görünür; ham metin İhlaller sekmesindedir."
      search={{ value: filterInput, onChange: setFilterInput, onSubmit: applyFilterText, placeholder: 'Sohbet veya mesaj ara…' }}
      actions={(
        <>
          <ModeTabs mode={mode} setMode={setMode} />
          <ColumnChooser table={table} />
          <button type="button" style={s.secondaryBtn}
            onClick={() => exportRows().then((data) => exportToExcel(data, 'yazisma-denetimi'))}>
            <ExcelIcon />Excel
          </button>
        </>
      )}
    >
      <C360Table
        table={table}
        loading={loading}
        rows={rows}
        selectedId={selected?.id}
        onRowClick={(row) => setSelected(row)}
        onRowDoubleClick={(row) => openDetail(row)}
        sortField={toGridSortField(sortField)}
        sortDir={sortDir}
        onSort={(field) => applySort(toODataSortField(field))}
        columnFilters={columnFilters}
        onColumnFilter={setColumnFilter}
        onClearColumnFilter={clearColumnFilter}
        emptyText={filterText ? 'Aramaya uygun sohbet yok.' : 'Henüz yazışma yok.'}
      />
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </ListPageShell>
  );
}

// ───────────────────────── İhlaller ─────────────────────────

const VIOLATION_FILTER_META = {
  senderName: { type: 'text', odataField: 'SenderName' },
  matchedWords: { type: 'text', odataField: 'MatchedWords' },
};

function ViolationsView({ mode, setMode, showToast }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const searchFields = useMemo(() => ['SenderName', 'MatchedWords'], []);

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    exportRows,
  } = useC360List({
    apiBase: API_VIOLATIONS,
    columnFilterMeta: VIOLATION_FILTER_META,
    searchFields,
    defaultSortField: 'CreatedDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('createdDate', {
      header: 'Zaman', enableColumnFilter: false,
      cell: (i) => fmtDateTime(i.getValue()),
    }),
    columnHelper.accessor('senderName', {
      header: 'Yazan', meta: { filterType: 'text' },
      cell: (i) => i.getValue() || '—',
    }),
    columnHelper.accessor('matchedWords', {
      header: 'Yakalanan Kelimeler', meta: { filterType: 'text' },
      cell: (i) => <span className="c360-erp-chip">{i.getValue() || '—'}</span>,
    }),
    // HAM METİN — bu ekran onu göstermek İÇİN vardır; mesaj akışında hiçbir zaman görünmez.
    columnHelper.accessor('originalBody', {
      header: 'Ham Metin', enableColumnFilter: false,
      cell: (i) => i.getValue() || '—',
    }),
    columnHelper.accessor('maskedBody', {
      header: 'İletilen (maskeli)', enableColumnFilter: false,
      cell: (i) => i.getValue() || '—',
    }),
    columnHelper.display({
      id: 'actions', header: '',
      cell: ({ row }) => (
        <button type="button" style={s.iconBtn} title="Sohbeti aç"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${LIST_ROUTE}/${row.original.conversationId}`);
          }}>
          <ViewIcon />
        </button>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  return (
    <ListPageShell
      title="Yazışma Denetimi"
      subtitle="Yasaklı kelime yakalanan mesajlar. Ham metin YALNIZ bu listede tutulur; mesaj akışı maskeli hâli gösterir."
      search={{ value: filterInput, onChange: setFilterInput, onSubmit: applyFilterText, placeholder: 'Yazan veya kelime ara…' }}
      actions={(
        <>
          <ModeTabs mode={mode} setMode={setMode} />
          <ColumnChooser table={table} />
          <button type="button" style={s.secondaryBtn}
            onClick={() => exportRows().then((data) => exportToExcel(data, 'yasakli-kelime-ihlalleri'))}>
            <ExcelIcon />Excel
          </button>
        </>
      )}
    >
      <C360Table
        table={table}
        loading={loading}
        rows={rows}
        selectedId={selected?.id}
        onRowClick={(row) => setSelected(row)}
        onRowDoubleClick={(row) => navigate(`${LIST_ROUTE}/${row.conversationId}`)}
        sortField={toGridSortField(sortField)}
        sortDir={sortDir}
        onSort={(field) => applySort(toODataSortField(field))}
        columnFilters={columnFilters}
        onColumnFilter={setColumnFilter}
        onClearColumnFilter={clearColumnFilter}
        emptyText={filterText ? 'Aramaya uygun ihlal yok.' : 'Yasaklı kelime ihlali kaydedilmedi.'}
      />
      <Pagination
        totalCount={totalCount}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
    </ListPageShell>
  );
}

const tabStyle = {
  padding: '7px 14px',
  borderRadius: 10,
  border: '1px solid var(--border-color,#d1d5db)',
  background: 'var(--bg-card,#fff)',
  color: 'var(--text-secondary,#6b7280)',
  fontSize: 13,
  cursor: 'pointer',
};
const tabActiveStyle = {
  ...tabStyle,
  background: 'var(--accent,#0073ea)',
  borderColor: 'var(--accent,#0073ea)',
  color: '#fff',
  fontWeight: 600,
};
